/**
 * Login сторінка.
 *
 * Обов'язковість термінала для цехових ролей керується фіче-флагом
 * `terminal_login_required` (Пульт супер-адміна → Функціонал → «Обов'язковий
 * вхід через термінал»), читається публічним `GET /auth/login-config`.
 * Стара логіка (термінал — єдиний спосіб входу, force-logout якщо зміна не
 * відкрита) НЕ видалена — просто не рендериться, поки прапор вимкнено.
 * Адмін вмикає/вимикає прямо з Пульта, без редеплою.
 *
 * Коли прапор ВИМКНЕНО (дефолт, 2026-07-08): вхід = свій qr_token (скан
 * бейджу або ручний ввід) → одразу self-checkin (`POST /attendance/checkin`,
 * той самий ендпоінт що й термінал використовує) + login
 * (`POST /auth/login/qr`) в одному тапі, без камери термінала. Геофенс
 * (WORKSHOP_LAT/LON на беку) лишається реальним захистом від "відмітився
 * з дому" — термінал ніколи не був цим захистом, лише посередником.
 *
 * Google OAuth (admin/master/менеджер) — без змін в обох режимах.
 *
 * localStorage ключ: `mes-device-qr` (qr_token свого бейджу).
 */
import { terminalKey } from "@/lib/terminalMode";
import { DEVICE_KEY as DEVICE_BADGE_KEY, deviceBadgeName, forgetDeviceBadge, rememberDeviceBadge } from "@/lib/deviceBadge";
import { lazy, Suspense, useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiClient, apiLoginRaw } from "@/api/client";
import { errorCode, errorMessage } from "@/lib/apiErrors";
import { useAuthStore, decodeJwt } from "@/store/auth";
import { toast } from "@/store/toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Сканер вантажимо ЛИШЕ коли його відкрили: html5-qrcode усередині важить
// ~370 КБ. Екран входу бачать усі, зокрема ті, хто заходить через Google і
// камери не торкається — тягнути їм сканер немає сенсу.
const QrScannerModal = lazy(() => import("@/components/QrScannerModal"));
import { extractQrToken } from "@/lib/qrToken";
import { getGeoCoords } from "@/lib/geo";
import { landingForRoles } from "@/lib/landing";
import type { LoginResponse } from "@/types/api";

interface LocationState {
  from?: string;
}

const DEVICE_KEY = DEVICE_BADGE_KEY;

const OAUTH_ERRORS: Record<string, string> = {
  oauth: "Помилка Google-авторизації. Спробуй ще раз.",
  no_email: "Google не повернув email. Перевір налаштування акаунту.",
  denied: "Цей email не має доступу до системи.",
  inactive: "Акаунт деактивовано. Звернись до адміна.",
  db: "Помилка БД при створенні користувача.",
};

const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

type ScannerMode = "off" | "terminal" | "register";

export default function LoginPage() {
  const [qrToken, setQrToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [scannerMode, setScannerMode] = useState<ScannerMode>("off");
  const [savedDeviceQr, setSavedDeviceQr] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState<string | null>(deviceBadgeName);
  const [showManualForm, setShowManualForm] = useState(false);
  // Фіче-флаг з Пульта (Функціонал → «Обов'язковий вхід через термінал»).
  // null = ще завантажується; трактуємо як false (без термінала), щоб не
  // блимнути старим UI перед відповіддю бекенду.
  const [terminalRequired, setTerminalRequired] = useState<boolean | null>(null);
  const requireTerminal = terminalRequired === true;
  // Причина невдалого входу — НА ЕКРАНІ. Тост зникає за кілька секунд, а
  // людина лишалась з єдиною кнопкою «Увійти», яка щоразу давала те саме.
  const [loginError, setLoginError] = useState<{ text: string; hint?: string } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);

  // Телефон-термінал (ТЗ2): замість звичайного входу — «Скануй бейдж».
  useEffect(() => {
    if (terminalKey()) navigate("/work-terminal", { replace: true });
  }, [navigate]);

  // Завантажуємо збережений qr_token з localStorage при mount.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DEVICE_KEY);
      if (saved && saved.length > 0) setSavedDeviceQr(saved);
    } catch {
      /* приватний режим / localStorage недоступний — flow як уперше */
    }
  }, []);

  // OAuth-error toast (як було)
  useEffect(() => {
    const err = searchParams.get("error");
    if (err) {
      toast.error(OAUTH_ERRORS[err] ?? `Помилка входу: ${err}`);
      searchParams.delete("error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Бейдж відсканували камерою телефона → сюди прийшли з ?qr=<token>
  // (роут /sewing/login/qr/:token). Авто-логін тим самим flow, що й сканер.
  // Чекаємо на terminalRequired, щоб піти правильною гілкою диспетчера.
  const [pendingQr, setPendingQr] = useState<string | null>(null);
  useEffect(() => {
    const qr = searchParams.get("qr");
    if (qr) {
      setPendingQr(qr);
      searchParams.delete("qr");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    if (pendingQr && terminalRequired !== null && !submitting) {
      setPendingQr(null);
      void registerDeviceAndLogin(pendingQr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQr, terminalRequired]);

  // Публічний фіче-флаг: чи обов'язковий термінал (керується з Пульта).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/v1/auth/login-config`);
        if (!cancelled) {
          setTerminalRequired(Boolean(r.data?.data?.terminal_login_required));
        }
      } catch {
        // Fail-open до зручнішого (без термінала) режиму — недоступність
        // цього ендпоінта не мусить блокувати вхід у цех.
        if (!cancelled) setTerminalRequired(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Куди йти після успішного логіну: якщо RequireAuth привів сюди з
  // конкретної сторінки (`state.from`) — повертаємось туди. Інакше —
  // рольовий landing (для швеї/ВТО це /sewing, її реальне робоче місце;
  // НІКОЛИ не хардкодимо /workspace — це старий Sprint 9a артефакт).
  const explicitFrom = (location.state as LocationState | null)?.from;
  function landingPathFor(accessToken: string): string {
    if (explicitFrom) return explicitFrom;
    return landingForRoles(decodeJwt(accessToken)?.roles);
  }
  const isRegistered = useMemo(() => Boolean(savedDeviceQr), [savedDeviceQr]);

  /** Best-effort геолокація (як TerminalScreen) — сервер все одно є джерелом
   *  істини для геофенсу; тут лише передаємо координати, якщо браузер дав. */
  /** Самостійна відмітка приходу (той самий /attendance/checkin, яким
   *  користується термінал) — БЕЗ термінал_session. Викликаємо сирим axios
   *  (не apiClient), бо перед логіном ще нема сесії й interceptor не мусить
   *  чіпати цей запит (401-refresh-redirect логіка там розрахована на вже
   *  залогінені сесії). Повертає true якщо можна продовжувати до логіну. */
  /** Показати причину і на екрані, і тостом: екран лишається, тост ловить
   *  погляд, якщо людина дивиться в інше місце. */
  function fail(text: string, hint?: string) {
    setLoginError({ text, hint });
    toast.error(text);
  }

  async function selfCheckin(cleanedToken: string): Promise<boolean> {
    const coords = await getGeoCoords();
    try {
      await axios.post(`${API_BASE}/api/v1/attendance/checkin`, {
        qr_token: cleanedToken,
        lat: coords?.lat,
        lon: coords?.lon,
      });
      return true;
    } catch (err) {
      if (!axios.isAxiosError(err) || !err.response) {
        fail("Сервер недоступний.", "Перевірте зв'язок і спробуйте ще раз.");
        return false;
      }
      const status = err.response.status;
      const detail = err.response.data?.error?.message ?? err.response.data?.detail;
      if (status === 401) {
        fail("Невірний QR-код бейджу.", "Перевірте, що сканували свій бейдж, а не картку замовлення.");
        return false;
      }
      if (status === 400) {
        fail(detail || "Не вдалось відмітитись.");
        return false;
      }
      if (status === 403) {
        if (errorCode(err) === "shift_closed_today") {
          // День уже завершено НЕЮ САМОЮ: системне автозакриття бек тепер
          // пускає назад, тож сюди доходить лише свідоме «Завершити день».
          // Кнопка «Увійти» сама по собі це не полагодить — пишемо прямо,
          // від кого залежить наступний крок.
          fail(
            errorMessage(err, "Ви вже завершили сьогодні робочий день."),
            "Попросіть майстра відкрити вам день — після цього натисніть «Увійти» ще раз.",
          );
          return false;
        }
        // geofence_denied / geofence_required — реальний захист лишається.
        fail(
          detail || "Поза територією цеху.",
          "Підійдіть ближче до цеху й дозвольте браузеру геолокацію.",
        );
        return false;
      }
      if (status === 409) {
        // "Зміна вже відкрита" — не помилка для нас, просто продовжуємо логін.
        return true;
      }
      fail(detail || "Не вдалось відмітитись.");
      return false;
    }
  }

  /** Об'єднаний flow: self-checkin (відкрити зміну) + login/qr (отримати
   *  JWT), одним тапом, без термінала. Зберігає пристрій у localStorage
   *  тільки після успішного логіну. */
  async function checkinThenLogin(rawToken: string) {
    const cleaned = extractQrToken(rawToken);
    if (!cleaned) {
      fail("Не розпізнано QR-код бейджу.", "Спробуйте сканувати ще раз або введіть токен вручну.");
      return;
    }
    setSubmitting(true);
    setLoginError(null);
    try {
      const ok = await selfCheckin(cleaned);
      if (!ok) return;

      const resp = await apiLoginRaw<LoginResponse>("/api/v1/auth/login/qr", {
        qr_token: cleaned,
      });
      try {
        rememberDeviceBadge(cleaned, resp.user.name);
        setSavedDeviceQr(cleaned);
        setDeviceName(resp.user.name);
      } catch {
        toast.warning("Пристрій не вдалось запам'ятати (приватний режим)");
      }
      setSession(
        {
          access_token: resp.access_token,
          refresh_token: resp.refresh_token,
          token_type: resp.token_type,
        },
        resp.user,
      );
      toast.success(`Вітаємо, ${resp.user.name}!`);
      navigate(landingPathFor(resp.access_token), { replace: true });
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) fail("Невірний QR-код бейджу.", "Спробуйте сканувати ще раз.");
      else fail(errorMessage(err, "Не вдалось увійти."), "Спробуйте ще раз за хвилину.");
    } finally {
      setSubmitting(false);
    }
  }

  /** Сканер відсканував QR з ВЕЛИКОГО екрана термінала (signed JWT). */
  async function submitTerminalScan(terminalSession: string) {
    if (!savedDeviceQr) {
      fail("Цей пристрій ще не зареєстровано.", "Спершу скануйте свій бейдж.");
      return;
    }
    setSubmitting(true);
    setLoginError(null);
    try {
      const resp = await apiClient.post<LoginResponse>(
        "/api/v1/auth/terminal-login",
        { terminal_session: terminalSession, qr_token: savedDeviceQr },
      );
      const data = resp.data;
      setSession(
        {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_type: data.token_type,
        },
        data.user,
      );
      toast.success(`Зміна відкрита. Вітаємо, ${data.user.name}!`);
      navigate(landingPathFor(data.access_token), { replace: true });
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const detail = axios.isAxiosError(err)
        ? err.response?.data?.error?.message ?? err.response?.data?.detail
        : undefined;
      if (status === 403 && errorCode(err) === "shift_closed_today") {
        fail(
          errorMessage(err, "Ви вже завершили сьогодні робочий день."),
          "Попросіть майстра відкрити вам день — після цього скануйте термінал ще раз.",
        );
      } else if (status === 400) {
        fail(detail || "QR терміналу не валідний.", "Скануйте свіжий код з екрана термінала.");
      } else if (status === 401) {
        // Зберігач qr_token вже невалідний (адмін регенерував або деактивував)
        fail("Цей пристрій більше не зареєстрований.", "Зареєструйте його своїм бейджем ще раз.");
        localStorage.removeItem(DEVICE_KEY);
        setSavedDeviceQr(null);
      } else {
        fail(errorMessage(err, "Помилка входу через термінал."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** СТАРА поведінка (до 2026-07-08, активна лише коли terminal_login_required
   *  увімкнено з Пульта): реєстрація пристрою через /login/qr БЕЗ self-checkin
   *  — якщо зміна не відкрита, force-logout з проханням піти до термінала.
   *  Не видалено навмисно — повертається автоматично разом з прапором. */
  async function registerDeviceAndLoginViaTerminal(badgeQrToken: string) {
    const cleaned = extractQrToken(badgeQrToken);
    if (!cleaned) {
      fail("Не розпізнано QR-код бейджу.", "Спробуйте сканувати ще раз або введіть токен вручну.");
      return;
    }
    setSubmitting(true);
    setLoginError(null);
    try {
      const resp = await apiLoginRaw<LoginResponse>(
        "/api/v1/auth/login/qr",
        { qr_token: cleaned },
      );
      try {
        rememberDeviceBadge(cleaned, resp.user.name);
        setSavedDeviceQr(cleaned);
        setDeviceName(resp.user.name);
      } catch {
        toast.warning("Пристрій не вдалось запам'ятати (приватний режим)");
      }
      setSession(
        {
          access_token: resp.access_token,
          refresh_token: resp.refresh_token,
          token_type: resp.token_type,
        },
        resp.user,
      );

      if (resp.user.kind === "seamstress") {
        let shiftOpen = false;
        try {
          const statusResp = await apiClient.get<{ data: { active: boolean } }>(
            "/api/v1/attendance/status",
          );
          shiftOpen = Boolean(statusResp.data?.data?.active);
        } catch {
          shiftOpen = true;
        }
        if (!shiftOpen) {
          useAuthStore.getState().logout();
          toast.success(
            "Пристрій зареєстровано! Тепер скани термінал щоб відкрити зміну.",
          );
          return;
        }
      }

      toast.success(`Пристрій зареєстрований. Вітаємо, ${resp.user.name}!`);
      navigate(landingPathFor(resp.access_token), { replace: true });
    } catch (err: unknown) {
      const status =
        (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        fail("Невірний QR-код бейджу.", "Спробуйте сканувати ще раз.");
      } else if (status === 403 && errorCode(err) === "shift_closed_today") {
        fail(
          errorMessage(err, "Ви вже завершили сьогодні робочий день."),
          "Попросіть майстра відкрити вам день — після цього спробуйте ще раз.",
        );
      } else {
        fail(errorMessage(err, "Не вдалось зареєструвати пристрій."), "Спробуйте ще раз.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** Реєстрація пристрою: сканер прочитав власний бейдж (або ручний ввід).
   *  Диспетчер за фіче-флагом: увімкнено → стара поведінка (термінал
   *  обов'язковий), вимкнено (дефолт) → self-checkin+login одним тапом. */
  async function registerDeviceAndLogin(badgeQrToken: string) {
    if (requireTerminal) await registerDeviceAndLoginViaTerminal(badgeQrToken);
    else await checkinThenLogin(badgeQrToken);
  }

  function forgetDevice() {
    forgetDeviceBadge();
    setSavedDeviceQr(null);
    setDeviceName(null);
    setLoginError(null);
    toast.info("Пристрій прибрано");
  }

  async function onManualSubmit(e: FormEvent) {
    e.preventDefault();
    if (!qrToken.trim()) {
      fail("Введіть QR-токен з бейджу.");
      return;
    }
    // Manual ввід — це теж реєстрація пристрою: токен зберігається у
    // localStorage для наступного швидкого входу. Симетрично до сканування
    // бейджу камерою.
    await registerDeviceAndLogin(qrToken);
  }

  // Що робити коли сканер успішно прочитав QR — залежить від mode.
  function onScanResult(text: string) {
    const mode = scannerMode;
    setScannerMode("off");
    if (mode === "terminal") void submitTerminalScan(text);
    // register: бейдж кодує URL …/sewing/login/qr/<token>; нормалізацію
    // робить registerDeviceAndLogin (приймає і URL, і голий токен).
    else if (mode === "register") void registerDeviceAndLogin(text);
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-mes-primary">MES</h1>
          <p className="text-mes-muted text-sm mt-1">Швейне виробництво</p>
        </div>

        {/* Причина невдалого входу — НА ЕКРАНІ, поки її не змінить наступна
            спроба. Раніше тут був лише тост: він зникав, і людина лишалась
            з кнопкою, яка мовчки не спрацьовувала. */}
        {loginError && (
          <div className="mb-4 rounded-2xl border border-red-300 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">{loginError.text}</p>
            {loginError.hint && (
              <p className="text-xs text-red-700 mt-1">{loginError.hint}</p>
            )}
          </div>
        )}

        {/* Головна CTA — залежить від реєстрації пристрою + фіче-флага термінала */}
        {isRegistered ? (
          <div className="bg-mes-surface rounded-2xl shadow-sm border border-gray-200 p-5 space-y-4">
            <div>
              <p className="text-sm font-medium text-mes-ink">
                {deviceName ? <>Цей телефон запамʼятав бейдж: <b>{deviceName}</b></> : "Пристрій зареєстрований"}
              </p>
              <p className="text-xs text-mes-muted mt-1">
                {requireTerminal
                  ? "Скануй термінал у цеху щоб відкрити зміну і зайти."
                  : "Термінал тимчасово не обов'язковий — можна зайти одразу."}
              </p>
            </div>
            {requireTerminal ? (
              <button
                type="button"
                onClick={() => setScannerMode("terminal")}
                disabled={submitting}
                className="w-full h-14 bg-mes-primary text-white text-base font-semibold rounded-lg shadow-sm hover:opacity-95 active:opacity-90 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                🏭 Сканувати термінал
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void checkinThenLogin(savedDeviceQr ?? "")}
                disabled={submitting}
                className="w-full h-14 bg-mes-primary text-white text-base font-semibold rounded-lg shadow-sm hover:opacity-95 active:opacity-90 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {submitting ? "Вхід..." : "✅ Увійти"}
              </button>
            )}
            <button
              type="button"
              onClick={forgetDevice}
              disabled={submitting}
              className="w-full h-11 text-xs text-mes-muted underline hover:text-mes-ink"
            >
              {deviceName ? `Це не ${deviceName} — увійти іншим бейджем` : "Увійти іншим бейджем"}
            </button>
          </div>
        ) : (
          <div className="bg-mes-surface rounded-2xl shadow-sm border border-gray-200 p-5 space-y-4">
            <div>
              <p className="text-sm font-medium text-mes-ink">
                Це новий пристрій
              </p>
              <p className="text-xs text-mes-muted mt-1">
                {requireTerminal
                  ? "Скануй свій бейдж — і потім зможеш заходити одним сканом термінала."
                  : "Скануй свій бейдж — і одразу зайдеш, без термінала."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setScannerMode("register")}
              disabled={submitting}
              className="w-full h-14 bg-mes-primary text-white text-base font-semibold rounded-lg shadow-sm hover:opacity-95 active:opacity-90 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              📷 Зареєструвати пристрій
            </button>
          </div>
        )}

        {/* Manual fallback — для тестів / коли камера не працює */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowManualForm((v) => !v)}
            className="text-xs text-mes-muted underline mx-auto block"
          >
            {showManualForm ? "Сховати" : "Ввести QR-токен вручну"}
          </button>
          {showManualForm && (
            <form
              onSubmit={onManualSubmit}
              className="mt-3 bg-mes-surface rounded-2xl shadow-sm border border-gray-200 p-4 space-y-3"
              noValidate
            >
              <input
                id="qr_token"
                name="qr_token"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={qrToken}
                onChange={(e) => setQrToken(e.target.value)}
                disabled={submitting}
                placeholder="Вставити токен з бейджу"
                className="w-full h-12 px-3 text-base rounded-lg border border-gray-300 focus:border-mes-primary focus:ring-2 focus:ring-mes-primary/20 focus:outline-none disabled:bg-gray-100"
              />
              <button
                type="submit"
                disabled={submitting || qrToken.trim().length === 0}
                className="w-full h-12 bg-mes-primary text-white text-sm font-semibold rounded-lg disabled:bg-gray-300"
              >
                {submitting
                  ? "Вхід..."
                  : isRegistered
                  ? "Замінити пристрій цим токеном"
                  : "Зареєструвати цим токеном"}
              </button>
            </form>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center my-5">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="px-3 text-xs text-mes-muted uppercase tracking-wide">або</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Google sign-in — для admin/master/менеджера */}
        <a
          href={`${API_BASE}/api/v1/auth/google/start`}
          className="w-full h-14 bg-white border border-gray-300 text-mes-ink text-base font-medium rounded-lg shadow-sm hover:bg-gray-50 active:opacity-90 flex items-center justify-center gap-3"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
          </svg>
          <span>Увійти через Google</span>
        </a>

        {/* Реєстрація — той самий Google-вхід. Якщо акаунта немає в системі,
            бек створює заявку і веде в кімнату очікування. Довільний email
            ніде не вводиться: особу підтверджує Google. */}
        <a
          href={`${API_BASE}/api/v1/auth/google/start`}
          className="w-full h-11 mt-3 text-mes-ink text-sm font-medium rounded-lg border border-dashed border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-2"
        >
          <span>Вперше тут? Зареєструватися</span>
        </a>

        <p className="text-center text-xs text-mes-muted mt-4">
          {requireTerminal ? "Швея: скан термінала" : "Швея: свій QR/токен"} · Адмін: Google
        </p>
      </div>

      {scannerMode !== "off" && (
        <ErrorBoundary
          onError={(err) => {
            toast.error(`Сканер впав: ${err.message}`);
            setScannerMode("off");
          }}
          fallback={null}
        >
          <Suspense fallback={null}>
            <QrScannerModal
              onResult={onScanResult}
              onClose={() => setScannerMode("off")}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}
